import { EventBus } from '@/plugins/event-bus'
import PurchaseOrder from 'api-client/PurchaseOrder'
import Customers from 'api-client/Customers'

const moduleState = {
  unloadingLocations: [],
  establishments: [],
  controlCodes: [],
  purchaseOrders: [],
  purchaseOrder: {
    Details: {
      Dealer: {
        EstablishmentID: '',
      },
      Car: {
        Amount: 0,
        ConfigurationCode: '',
      },
      ShippingLocation: {
        ShippingLocationID: '',
        DeliveredToDealerNumber: '',
      },
      Customer: null,
      ControlCodes: [],
      Employee: {},
      Remark: '',
      OrderReference: '',
      CustomerType: {},
      CustomerCompany: null,
      LeaseCompany: null,
      DeliveryDate: null,
      DoNotDeliverBeforeDate: null,
      DeliverAsSoonAsPossible: true,
    },
    MetaData: {},
    PurchaseOrderId: '',
    CarStatuses: [],
    DeliveryDates: {},
  },
  formIsDirty: false,
}
const requiredFieldsForCustomer = [
  'CustomerFirstName',
  'CustomerFamilyName',
  'CustomerStreet',
  'CustomerHouseNumber',
  'CustomerZipCode',
  'CustomerCity',
]

const getters = {
  customerType: (state) => state.purchaseOrder.Details.CustomerType,
  unloadingLocations: (state) => state.unloadingLocations,
  establishments: (state) => state.establishments,
  controlCodes: (state) => state.controlCodes,
  all: (state) => state.purchaseOrders,
  get: (state) => state.purchaseOrder,
  customerHasAllRequiredFields: (state) => {
    const { Customer } = state.purchaseOrder.Details

    return (
      Customer &&
      Customer.CustomerSource === 'MANUAL' &&
      requiredFieldsForCustomer.every((field) => !!Customer[field])
    )
  },
  customerHasSomeRequiredFields: (state) => {
    const { Customer } = state.purchaseOrder.Details

    return (
      Customer &&
      Customer.CustomerSource === 'MANUAL' &&
      requiredFieldsForCustomer.some((field) => !!Customer[field])
    )
  },
  hasCustomer: (state) => {
    const { Customer } = state.purchaseOrder.Details

    return Customer && requiredFieldsForCustomer.some((field) => !!Customer[field])
  },
  isCustomerValid: (state) => {
    const customerCompany =
      state.purchaseOrder.Details.CustomerCompany &&
      Object.keys(state.purchaseOrder.Details.CustomerCompany).length > 0

    const leaseCompany =
      state.purchaseOrder.Details.LeaseCompany &&
      Object.keys(state.purchaseOrder.Details.LeaseCompany).length > 0

    const hasCustomer = getters.customerHasSomeRequiredFields(state)

    if (customerCompany || leaseCompany) {
      return !hasCustomer || getters.customerHasAllRequiredFields(state)
    } else {
      return (
        state.purchaseOrder.Details.Customer?.CustomerSource !== 'MANUAL' ||
        getters.customerHasAllRequiredFields(state)
      )
    }
  },
  hasCustomerType: (state) => {
    const { Name, Id } = getters.customerType(state)
    return !!Name || !!Id
  },
  customer: (state) => state.purchaseOrder.Details.Customer,
  customerCompany: (state) => state.purchaseOrder.Details.CustomerCompany,
  leaseCompany: (state) => state.purchaseOrder.Details.LeaseCompany,
  hasLeaseCompany: (state) =>
    state.purchaseOrder.Details.LeaseCompany &&
    Object.keys(state.purchaseOrder.Details.LeaseCompany).length > 0,
  hasCustomerCompany: (state) =>
    state.purchaseOrder?.Details?.CustomerCompany?.CustomerCompanyName != null &&
    state.purchaseOrder?.Details?.CustomerCompany?.CustomerCompanyName !== '',
  expectedDeliveryDates: (state) => state.purchaseOrder.DeliveryDates,
  deliveryDate: (state) => state.purchaseOrder.Details.DeliveryDate,
  doNotDeliverBeforeDate: (state) => state.purchaseOrder.Details.DoNotDeliverBeforeDate,
  configurationCode: (state) => state.purchaseOrder.Details.Car.ConfigurationCode,
  hasOwner: (state) =>
    getters.hasCustomer(state) ||
    getters.hasCustomerCompany(state) ||
    getters.hasLeaseCompany(state),
}

const mutations = {
  /* eslint-disable no-param-reassign */
  initNewPurchaseOrder(state, employee) {
    state.purchaseOrder = {
      Details: {
        Dealer: {},
        Car: {},
        ShippingLocation: {},
        Customer: {},
        ControlCodes: [],
        Employee: {
          PonId: employee.PonId,
          EmployeeName: employee.EmployeeName,
          EmployeeEmail: employee.EmployeeEmail,
        },
        Remark: '',
        CustomerType: {},
        CustomerCompany: {},
        LeaseCompany: {},
        DeliveryDate: null,
        DoNotDeliverBeforeDate: null,
        DeliverAsSoonAsPossible: true,
      },
      MetaData: {},
      PurchaseOrderId: '',
      CarStatuses: [],
      DeliveryDates: {},
    }
  },
  setUnloadingLocations: (state, payload) => {
    state.unloadingLocations = payload
    EventBus.$emit('purchaseOrder/unloadingLocations')
  },
  setEstablishments: (state, payload) => {
    state.establishments = payload
    EventBus.$emit('purchaseOrder/establishments')
  },
  setControlCodes: (state, payload) => {
    state.controlCodes = payload
    state.purchaseOrder.Details.ControlCodes = state.controlCodes
    EventBus.$emit('purchaseOrder/controlCodes')
  },
  removeFromControlCodes: (state, payload) => {
    state.controlCodes = state.controlCodes.filter((c) => c.Id !== payload.Id)
    state.purchaseOrder.Details.ControlCodes = state.controlCodes
    EventBus.$emit('purchaseOrder/controlCodes')
  },
  addToControlCodes: (state, payload) => {
    state.controlCodes.push(payload)
    state.purchaseOrder.Details.ControlCodes = state.controlCodes
    EventBus.$emit('purchaseOrder/controlCodes')
  },
  setPurchaseOrders: (state, payload) => {
    state.purchaseOrders = payload
    EventBus.$emit('purchaseOrder/all')
  },
  setPurchaseOrder: (state, payload) => {
    state.purchaseOrder = payload
  },
  setUnloadingLocation: (state, payload) => {
    state.purchaseOrder.Details.ShippingLocation.ShippingLocationID = payload?.ShippingLocationID
    state.purchaseOrder.Details.ShippingLocation.DeliveredToDealerNumber =
      payload.DeliveredToDealerNumber
  },
  setEstablishmentId: (state, payload) => {
    state.purchaseOrder.Details = {
      ...state.purchaseOrder.Details,
      Dealer: {
        ...state.purchaseOrder.Details.Dealer,
        EstablishmentID: payload,
      },
    }
  },
  setCarAmount: (state, payload) => {
    state.purchaseOrder.Details = {
      ...state.purchaseOrder.Details,
      Car: { ...state.purchaseOrder.Details.Car, Amount: payload },
    }
    state.formIsDirty = true
  },
  setRemark: (state, payload) => {
    state.purchaseOrder.Details.Remark = payload
    state.formIsDirty = true
  },
  setDispoNote: (state, payload) => {
    state.purchaseOrder.Details.OrderReference = payload
    state.formIsDirty = true
  },
  setCustomer: (state, payload) => {
    state.purchaseOrder.Details.Customer = payload
    state.formIsDirty = true
  },
  setDoNotDeliverBeforeDate: (state, payload) => {
    if (!payload) {
      state.purchaseOrder.Details.DoNotDeliverBeforeDate = null
      state.purchaseOrder.Details.DeliverAsSoonAsPossible = true
    } else {
      state.purchaseOrder.Details.DoNotDeliverBeforeDate = payload
      state.purchaseOrder.Details.DeliverAsSoonAsPossible = false
    }
    state.formIsDirty = true
  },
  setExpectedDeliveryDates: (state, payload) => {
    state.purchaseOrder.DeliveryDates = payload
    EventBus.$emit('delivery-dates-loaded')
  },
  setDeliveryDate: (state, payload) => {
    state.purchaseOrder.Details.DeliveryDate = payload
    state.formIsDirty = true
  },
  setConfigurationCode: (state, payload) => {
    state.purchaseOrder.Details.Car.ConfigurationCode = payload
    state.formIsDirty = true
  },
  setFormNotDirty: (state) => {
    state.formIsDirty = false
  },
  setCustomerSource: (state, payload) => {
    if (!state.purchaseOrder.Details.Customer) {
      state.purchaseOrder.Details.Customer = {}
    }
    state.purchaseOrder.Details.Customer.CustomerSource = payload
  },
  setCustomerType: (state, payload) => {
    state.purchaseOrder.Details.CustomerType = payload
  },
  setCustomerCompany: (state, payload) => {
    state.purchaseOrder.Details.CustomerCompany = payload
  },
  setLeaseCompany: (state, payload) => {
    state.purchaseOrder.Details.LeaseCompany = payload
  },
  resetCustomer: (state, payload) => {
    state.purchaseOrder.Details.Customer = payload
  },
  resetCustomerCompany: (state, payload) => {
    state.purchaseOrder.Details.CustomerCompany = payload
  },
  resetLeaseCompany: (state, payload) => {
    state.purchaseOrder.Details.LeaseCompany = payload
  },
}

const actions = {
  setCustomer: ({ commit }, customer) => {
    commit('setCustomer', customer)
  },
  setCustomerSource: ({ commit }, source) => {
    commit('setCustomerSource', source)
  },
  initNewPurchaseOrder: ({ commit }, employee) => {
    commit('initNewPurchaseOrder', employee)
  },
  getUnloadingLocations: ({ commit }) => {
    PurchaseOrder.getUnloadingLocations().then((response) => {
      commit('setUnloadingLocations', response.data || [])
    })
  },
  getEstablishments: ({ commit }) => {
    PurchaseOrder.getEstablishments().then((response) => {
      commit('setEstablishments', response.data || [])
    })
  },
  addControlCode: ({ commit }, payload) => {
    commit('addToControlCodes', payload)
  },
  removeControlCode: ({ commit }, payload) => {
    commit('removeFromControlCodes', payload)
  },
  setControlCodes: ({ commit }, payload) => {
    commit('setControlCodes', payload)
  },
  addPurchaseOrders: ({ commit, state }, payload) => {
    let concatted = state.purchaseOrders.concat(payload)
    commit('setPurchaseOrders', concatted)
  },
  resetPurchaseOrders: ({ commit }) => {
    commit('setPurchaseOrders', [])
  },
  setPurchaseOrder: ({ commit }, payload) => {
    commit('setPurchaseOrder', payload)
    commit('setFormNotDirty')
  },
  setCustomerTypeId: ({ commit }, payload) => {
    const customerType = { Id: payload }
    commit('setCustomerType', customerType)
  },
  setCustomerType: ({ commit }, payload) => {
    commit('setCustomerType', payload)
  },
  setCustomerCompany: async ({ commit, state }, payload) => {
    const customerCompany = payload ? payload : null

    commit('setCustomerCompany', customerCompany)

    // If we have a purchase order ID and are adding a company, use the new API
    const purchaseOrderId = state.purchaseOrder.PurchaseOrderId
    if (purchaseOrderId && customerCompany && customerCompany.CustomerCompanyMagicNumber) {
      try {
        await Customers.addCustomerCompany(
          purchaseOrderId,
          customerCompany.CustomerCompanyMagicNumber,
          customerCompany.CustomerCompanySource
        )
      } catch (error) {
        console.error('Failed to add customer company to purchase order:', error)
        // Revert the commit if API call fails
        commit('setCustomerCompany', null)
        throw error
      }
    }
  },
  setLeaseCompany: async ({ commit, state }, payload) => {
    const leaseCompany = payload ? payload : null
    commit('setLeaseCompany', leaseCompany)

    // If we have a purchase order ID and are adding a lease company, use the new API
    const purchaseOrderId = state.purchaseOrder.PurchaseOrderId
    if (purchaseOrderId && leaseCompany && leaseCompany.CustomerCompanyMagicNumber) {
      try {
        await Customers.addLeaseCompany(
          purchaseOrderId,
          leaseCompany.CustomerCompanyMagicNumber,
          leaseCompany.CustomerCompanySource
        )
      } catch (error) {
        console.error('Failed to add lease company to purchase order:', error)
        // Revert the commit if API call fails
        commit('setLeaseCompany', null)
        throw error
      }
    }
  },
  resetCustomer: ({ commit }, payload = {}) => {
    commit('resetCustomer', payload)
  },
  resetCustomerCompany: async ({ commit, state }, payload = {}) => {
    const purchaseOrderId = state.purchaseOrder.PurchaseOrderId

    // If we have a purchase order ID, use the new remove API
    if (purchaseOrderId) {
      try {
        await Customers.removeCompany(purchaseOrderId)
      } catch (error) {
        console.error('Failed to remove customer company from purchase order:', error)
        throw error
      }
    }

    commit('resetCustomerCompany', payload)
  },
  resetLeaseCompany: async ({ commit, state }, payload = {}) => {
    const purchaseOrderId = state.purchaseOrder.PurchaseOrderId

    // If we have a purchase order ID, use the new remove API
    if (purchaseOrderId) {
      try {
        await Customers.removeLeaseCompany(purchaseOrderId)
      } catch (error) {
        console.error('Failed to remove lease company from purchase order:', error)
        throw error
      }
    }

    commit('resetLeaseCompany', payload)
  },
  setDeliveryDate: ({ commit }, payload) => {
    commit('setDeliveryDate', payload)
  },
  setDoNotDeliverBeforeDate: ({ commit }, payload) => {
    commit('setDoNotDeliverBeforeDate', payload)
  },
  setExpectedDeliveryDates: ({ commit }, payload) => {
    commit('setExpectedDeliveryDates', payload)
  },
}

export default {
  state: moduleState,
  getters,
  mutations,
  actions,
  namespaced: true,
}
